/**
 * Category mapping — Jasr Hub categories ↔ Zoho chart of accounts
 */
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import {
  useZohoCategories, useZohoAccounts, useZohoSaveMappings, useZohoStatus,
} from '@/hooks/useZoho';
import { Save, Loader2, Tags } from 'lucide-react';

export function ZohoCategoryMappingCard() {
  const { t } = useTranslation();
  const status = useZohoStatus();
  const cats = useZohoCategories();
  const accs = useZohoAccounts();
  const save = useZohoSaveMappings();
  const [mappings, setMappings] = useState<Record<string, string | null>>({});
  const [saveMsg, setSaveMsg] = useState<string | null>(null);

  // Initialize mappings from current data
  useEffect(() => {
    if (cats.data) {
      const m: Record<string, string | null> = {};
      for (const c of cats.data) m[c.id] = c.zoho_account_id;
      setMappings(m);
    }
  }, [cats.data]);

  if (!status.data?.connected) return null;

  if (!accs.data || accs.data.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-muted/30 p-6 text-center text-sm text-muted-foreground">
        {t('zoho.map_no_accounts', 'Sync chart of accounts first to enable mapping.')}
      </div>
    );
  }

  const handleChange = (categoryId: string, value: string) => {
    setMappings(prev => ({ ...prev, [categoryId]: value === '' ? null : value }));
    setSaveMsg(null);
  };

  const handleSave = async () => {
    setSaveMsg(null);
    try {
      const list = Object.entries(mappings).map(([cid, aid]) => ({
        category_id: cid,
        zoho_account_id: aid,
      }));
      const r = await save.mutateAsync(list);
      setSaveMsg(t('zoho.mappings_saved', '{{count}} mappings saved', { count: r.saved }));
    } catch (e) {
      setSaveMsg(`✗ ${e instanceof Error ? e.message : 'Save failed'}`);
    }
  };

  const dirty = cats.data?.some(c => mappings[c.id] !== c.zoho_account_id) ?? false;

  return (
    <div className="rounded-lg border border-border bg-card p-6 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Tags className="h-5 w-5" />
            {t('zoho.map_title', 'Map categories to Zoho accounts')}
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            {t('zoho.map_desc', 'Each Jasr Hub expense category maps to a Zoho expense account for accounting.')}
          </p>
        </div>
        <Button onClick={handleSave} disabled={!dirty || save.isPending} variant="gold" size="sm">
          {save.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          {t('zoho.save_mappings', 'Save mappings')}
        </Button>
      </div>

      {cats.isLoading ? (
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      ) : (
        <div className="space-y-2">
          {cats.data?.map(c => (
            <div key={c.id} className="grid grid-cols-1 sm:grid-cols-2 gap-2 items-center py-1 border-b border-border/50 last:border-0">
              <div>
                <span className="font-mono text-xs text-muted-foreground">{c.code}</span>
                <span className="ml-2 text-sm">{c.name_en}</span>
              </div>
              <select
                value={mappings[c.id] ?? ''}
                onChange={e => handleChange(c.id, e.target.value)}
                className="rounded-md border border-border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">{t('zoho.map_unmapped', '— not mapped —')}</option>
                {accs.data?.map(a => (
                  <option key={a.zoho_account_id} value={a.zoho_account_id}>
                    {a.account_name}
                    {a.account_code ? ` (${a.account_code})` : ''}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>
      )}

      {saveMsg && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          {saveMsg}
        </div>
      )}
    </div>
  );
}
